import { Add, Close } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import React, { FC, useEffect, useState } from 'react';
import { UploadIcon } from 'src/components/ui/UploadIcon';
import { Colors } from 'src/components/utils/Theme';
import { Attachment } from 'src/models';
import { object, string, array, date } from 'yup';
import { postNewInstantVoting, postNewVoting } from 'src/api/stoApi';
import { generalStore } from 'src/stores/GeneralStore';
import { useTranslation } from 'react-i18next';
import { DefaultTextAreaField } from 'src/components/mui-overrides/Textarea';
import { DefaultTextField } from 'src/components/mui-overrides/Textfield';
import { NormalDatepicker } from '../../mui-overrides/Datepicker';

interface Props {
  open: boolean;
  handleClose: () => void;
  reload: () => void;
  assetId: string;
  username: string;
}

type VotingType = 'instant' | 'custom' | null;
const votingTypes: VotingType[] = ['instant', 'custom'];

export const AddVotingDialog: FC<Props> = ({ open, handleClose, reload, assetId, username }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [votingType, setVotingType] = useState<VotingType>(null);
  const { t } = useTranslation();
  const tomorrow = new Date(new Date().getTime() + 1000 * 60 * 60 * 24);
  tomorrow.setHours(0, 0, 0, 0);

  const validationSchema = object({
    title: string().required(t('dashboard.assets.forum.addvoting.title.required')),
    description: string().required(t('dashboard.assets.forum.addvoting.description.required')),
    options: array().of(
      object().shape({
        title: string().required(t('dashboard.assets.forum.addvoting.option.title.required')),
        text: string().required(t('dashboard.assets.forum.addvoting.option.text.required')),
        attachments: array(),
      }),
    ),
    endDate: date()
      .nullable()
      .min(tomorrow, t('dashboard.assets.forum.addvoting.endDate.tooEarly'))
      .typeError(t('dashboard.assets.forum.addvoting.endDate.invalid')),
  });

  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      title: '',
      description: '',
      options: [
        {
          title: '',
          text: '',
          attachments: [] as Attachment[],
        },
      ],
      endDate: null,
    },
    validationSchema,
    onSubmit: async (values) => {
      console.log(values);
      setIsLoading(true);
      try {
        if (votingType === 'instant') {
          await postNewInstantVoting({
            assetId: assetId,
            userName: username,
            title: values.title,
            text: values.description,
            endDate: values.endDate,
          });
        } else if (votingType === 'custom') {
          await postNewVoting({
            assetId: assetId,
            userName: username,
            title: values.title,
            text: values.description,
            options: values.options,
          });
        }
        reload();
        handleCloseAndReset();
      } catch (error) {
        generalStore.setError(t('dashboard.assets.forum.addvoting.error'), error);
      } finally {
        setIsLoading(false);
      }
    },
  });

  const handleCloseAndReset = () => {
    handleClose();
    formik.resetForm();
  };

  const addOptionClicked = () => {
    formik.setFieldValue('options', [
      ...formik.values.options,
      {
        title: '',
        description: '',
        attachments: [],
      },
    ]);
  };

  const deleteOptionClicked = (index: number) => {
    const options = [...formik.values.options];
    options.splice(index, 1);
    formik.setFieldValue('options', options);
  };

  const chooseType = (type: VotingType) => {
    setVotingType(type);
    if (type === 'instant') {
      formik.setFieldValue('options', []);
    }
  };

  useEffect(() => {
    if (open) setVotingType(null);
  }, [open]);

  let dialogTitle = t('dashboard.assets.forum.addvoting.headline');
  let dialogContent = (
    <>
      {votingTypes.map((type) =>
        type === null ? (
          false
        ) : (
          <Card sx={{ mt: 1 }}>
            <CardContent>
              <Typography variant="h5" component="div">
                {t(`dashboard.assets.forum.addvoting.choose.${type}.title`)}
              </Typography>
              <Typography color="text.secondary" sx={{ 'white-space': 'pre-line' }}>
                {t(`dashboard.assets.forum.addvoting.choose.${type}.description`)}
              </Typography>
            </CardContent>
            <CardActions>
              <Button variant="contained" size="small" onClick={() => chooseType(type)} data-cy={`create-${type}-voting`}>
                {t('dashboard.assets.forum.addvoting.create.label')}
              </Button>
            </CardActions>
          </Card>
        ),
      )}
    </>
  );
  if (votingType === 'instant') {
    dialogTitle = t('dashboard.assets.forum.addvoting.instant.headline');
    dialogContent = (
      <>
        <DefaultTextField label={t('dashboard.assets.forum.addvoting.title.label')} fieldName={'title'} formik={formik} />
        <DefaultTextAreaField fieldName="description" label={t('dashboard.assets.forum.addvoting.description.label')} formik={formik} />
        <NormalDatepicker
          minDate={tomorrow}
          label={t('dashboard.assets.forum.addvoting.endDate.label')}
          fieldName="endDate"
          formik={formik}
        />
      </>
    );
  } else if (votingType === 'custom') {
    dialogTitle = t('dashboard.assets.forum.addvoting.custom.headline');
    dialogContent = (
      <>
        <DefaultTextField label={t('dashboard.assets.forum.addvoting.title.label')} fieldName={'title'} formik={formik} />
        <DefaultTextAreaField fieldName="description" label={t('dashboard.assets.forum.addvoting.description.label')} formik={formik} />

        {formik.values.options.map((option, index) => (
          <ForumOption
            key={index}
            formik={formik}
            index={index}
            deleteClicked={() => deleteOptionClicked(index)}
            showDelete={index > 0}
            deleteFileClicked={(index: number) => {
              const attachments = [...(formik.values.options[index] as any).attachments];
              attachments.splice(index, 1);
              formik.setFieldValue(`options[${index}].attachments`, attachments);
            }}
            newFile={(attachment: Attachment) => {
              formik.setFieldValue(`options[${index}].attachments`, [...(formik.values.options[index] as any).attachments, attachment]);
            }}
          />
        ))}

        <Button variant="contained" startIcon={<Add />} fullWidth onClick={addOptionClicked} data-cy="add-option-button">
          {t('dashboard.assets.forum.addvoting.addoption')}
        </Button>
      </>
    );
  }

  return (
    <Dialog open={open} onClose={handleCloseAndReset} fullWidth maxWidth="sm">
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>{dialogContent}</DialogContent>
        <DialogActions>
          <Button type="button" color="inherit" onClick={handleCloseAndReset}>
            {t('dashboard.assets.forum.addvoting.cancel')}
          </Button>
          {votingType !== null && (
            <LoadingButton
              loading={isLoading}
              type="submit"
              variant="contained"
              disableElevation
              color="primary"
              data-cy="add-voting-button"
            >
              {t('dashboard.assets.forum.addvoting.publish')}
            </LoadingButton>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};

interface ForumOptionProps {
  index: number;
  formik: any;
  showDelete: boolean;
  deleteClicked: () => void;
  newFile: (attachment: Attachment) => void;
  deleteFileClicked: (index: number) => void;
}

const ForumOption: FC<ForumOptionProps> = ({ index, formik, showDelete, deleteClicked, deleteFileClicked, newFile }) => {
  const { t } = useTranslation();
  return (
    <div style={{ marginBottom: '14px' }}>
      <Typography variant="h6">
        {t('dashboard.assets.forum.addvoting.option.headline')} {index + 1}
      </Typography>
      <DefaultTextField
        label={t('dashboard.assets.forum.addvoting.option.title.label')}
        fieldName={`options[${index}].title`}
        fieldList={['options', index.toString(), 'title']}
        formik={formik}
      />
      <DefaultTextAreaField
        fieldName={`options[${index}].text`}
        fieldList={['options', index.toString(), 'text']}
        label={t('dashboard.assets.forum.addvoting.option.text.label')}
        formik={formik}
      ></DefaultTextAreaField>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, paddingBottom: 6 }}>
        {formik.values.options[index].attachments.map((attachment: Attachment, index: number) => (
          <div style={{ backgroundColor: Colors.E5GREY, borderRadius: 6, padding: '0 6px' }} key={index}>
            <Typography variant="caption">{attachment.name}</Typography>
            <IconButton size="small" onClick={() => deleteFileClicked(index)}>
              <Close />
            </IconButton>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <UploadIcon newFile={newFile} />
        {showDelete && (
          <Button color="inherit" onClick={deleteClicked}>
            {t('dashboard.assets.forum.addvoting.option.deleteoption')}
          </Button>
        )}
      </div>
    </div>
  );
};
